"use client";

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Add } from "iconsax-react";
import { TUser } from "@/actions/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import getInitials from "@/lib/getInitials";
import Link from "next/link";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/lang.json";
import { toggleFollowProfileById, getMySuggestedUsers } from "@/actions/profiles";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { publicProfileHref } from "@/lib/localePath";

type SuggestedUsersProps = {
  users: TUser[];
  className?: string;
  locale?: SupportedLanguage;
};

export default function SuggestedUsers({
  users: initialUsers,
  className,
  locale = "en",
}: SuggestedUsersProps) {
  const [users, setUsers] = useState<TUser[]>(initialUsers);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const handleFollow = async (userId: string) => {
    if (loadingIds.has(userId)) return;
    setLoadingIds((prev) => new Set(prev).add(userId));

    try {
      const followedUser = users.find((u) => u.id === userId);
      const result = await toggleFollowProfileById(userId);

      if (result.status === "success") {
        toast.success(`Successfully followed ${followedUser?.name ?? "user"}`);

        const suggested = await getMySuggestedUsers(1);
        if (suggested.status === "success" && suggested.metaData.length > 0) {
          setUsers((prev) =>
            prev.map((u) => (u.id === userId ? suggested.metaData[0] : u))
          );
        } else {
          setUsers((prev) => prev.filter((u) => u.id !== userId));
        }
      } else {
        toast.error(result.message);
      }
    } catch {
      toast.error("Failed to follow user");
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold mb-4">{lang["suggested-to-follow"][locale]}</h3>
      <div className="flex flex-col gap-4">
        {users.map((user) => (
          <SuggestedUserCard
            key={user.id}
            user={user}
            locale={locale}
            onFollow={handleFollow}
            loading={loadingIds.has(user.id)}
          />
        ))}
      </div>
    </div>
  );
}

type SuggestedUserCardProps = {
  user: TUser;
  locale?: SupportedLanguage;
  onFollow: (userId: string) => void;
  loading?: boolean;
};

function SuggestedUserCard({ user, locale = "en", onFollow, loading }: SuggestedUserCardProps) {
  const initials = getInitials(user.name);

  return (
    <div className={cn(
      "flex items-center justify-between transition-opacity",
      loading && "opacity-40 pointer-events-none"
    )}>
      <div className="flex items-center gap-3 relative">
        <div className="relative h-12 w-12 rounded-full overflow-hidden">
          <Avatar className="w-full h-full rounded-full overflow-hidden">
            {user.image && (
              <AvatarImage src={user.image} className="object-cover" />
            )}
            <AvatarFallback className="bg-gray-900 text-white text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="flex flex-col">
          <h4 className="font-medium">{user.name}</h4>
          <p className="text-sm text-gray-500">{user.title}</p>
        </div>
        <Link
          href={publicProfileHref(locale, user.id)}
          className="absolute inset-0 z-0"
          aria-label={user.name}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="relative z-10 rounded-full"
        onClick={() => onFollow(user.id)}
      >
        <Add size={16} className="mr-1 stroke-current" variant="Linear" />
        {lang["follow"][locale]}
      </Button>
    </div>
  );
}
